<template>
  <Alert
    v-if="isArchivedProcedure"
    class="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
  >
    <AlertTitle>
      You are viewing a deprecated version of this procedure.
    </AlertTitle>
    <AlertDescription>
      <p v-if="hasLatestPublishedLink">
        <router-link
          class="underline underline-offset-4"
          :to="{
            name: 'ProcedureShowPage',
            params: {
              businessSystem: businessSystemId,
              procedure: latestPublishedProcedure.id,
            },
          }"
        >
          Open latest published version (v{{ latestPublishedProcedure.version }}).
        </router-link>
      </p>
      <p v-else>
        There is no newer published version available yet.
      </p>
    </AlertDescription>
  </Alert>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { Alert, AlertDescription, AlertTitle } from '../../../../../../../resources/js/components/ui/alert/index.js'
import { useProcedureStore } from '../../stores/procedures.js'

const props = defineProps({
  procedure: {
    type: Object,
    default: null,
  },
})

const procedureStore = useProcedureStore()
const procedureId = computed(() => Number(props.procedure?.id ?? 0))
const businessSystemId = computed(() => Number(props.procedure?.business_system_id ?? 0))
const historyEntries = computed(() => {
  return procedureStore.getProcedureHistory(procedureId.value)
})
const isArchivedProcedure = computed(() => props.procedure?.status === 'archived')
const latestPublishedProcedure = computed(() => {
  const publishedEntries = historyEntries.value.filter((entry) => entry?.status === 'published')

  if (publishedEntries.length === 0) {
    return null
  }

  return publishedEntries.reduce((latestEntry, currentEntry) => {
    if (!latestEntry) {
      return currentEntry
    }

    const latestVersion = Number(latestEntry.version)
    const currentVersion = Number(currentEntry.version)

    if (Number.isFinite(currentVersion) && Number.isFinite(latestVersion) && currentVersion > latestVersion) {
      return currentEntry
    }

    if (currentVersion === latestVersion && Number(currentEntry.id) > Number(latestEntry.id)) {
      return currentEntry
    }

    return latestEntry
  }, null)
})
const hasLatestPublishedLink = computed(() => {
  return isArchivedProcedure.value
    && latestPublishedProcedure.value !== null
    && Number(latestPublishedProcedure.value.id) !== procedureId.value
})

const fetchHistory = async () => {
  if (!isArchivedProcedure.value || !Number.isFinite(procedureId.value) || procedureId.value <= 0) {
    return
  }

  await procedureStore.fetchProcedureHistory(procedureId.value)
}

onMounted(() => {
  fetchHistory()
})

watch([procedureId, isArchivedProcedure], () => {
  fetchHistory()
})
</script>
